import React from "react";
import DataTableRow from "../DataTableRow";
import DataTableCell from "../DataTableCell";
import DataTableEmptyState from "../DataTableEmptyState";

/**
 * Resolves the value of a row column based on the accessor provided.
 *
 * If accessor is a function, it is called with the row as an argument and
 * the return value is used as the resolved value.
 *
 * If accessor is a string, it is expected to be a dot-separated path to
 * the value in the row. For example, "address.street" would look for a
 * "street" property in the "address" object in the row.
 *
 * @param {Object} row - The row object.
 * @param {(string|Function)} accessor - The accessor string or function.
 * @returns {*} The resolved value.
 */
const resolveValue = (row, accessor) => {
  if (!accessor) return null;
  return typeof accessor === "function"
    ? accessor(row)
    : accessor.split(".").reduce((acc, key) => acc?.[key], row);
};

/**
 * DataTableBody
 *
 * The table body component renders the rows of the table. If the rows
 * prop is empty, it renders a single row with a single cell that spans
 * all columns with the text "No records found.".
 *
 * @param {Object[]} rows - The array of rows to render.
 * @param {Object[]} columns - The array of columns. Each column should
 * have an accessor property that defines how to access the value in the
 * row object.
 * @param {Function} [onRowClick] - Optional function that is called when
 * a row is clicked.
 * @param {Object} [query] - Query object containing page and pageSize for pagination.
 * @returns {React.ReactNode} The rendered table body.
 */
const DataTableBody = ({ rows, columns, onRowClick, query }) => {
  if (!rows || rows.length === 0) {
    return (
      <tbody>
        <DataTableEmptyState columns={columns} />
      </tbody>
    );
  }

  return (
    <tbody>
      {rows.map((row, rowIndex) => (
        <DataTableRow
          key={row.id || rowIndex}
          onRowClick={onRowClick}
          rowData={row}
          rowIndex={rowIndex}
        >
          {columns.map((col) => {
            // Calculate sequential row number accounting for pagination
            const sequentialRowNumber = query 
              ? (query.page - 1) * query.pageSize + rowIndex + 1 
              : rowIndex + 1;
            
            return (
              <DataTableCell key={col.id}>
                {col.cell
                  ? col.cell(resolveValue(row, col.accessor), row, rowIndex, sequentialRowNumber)
                  : resolveValue(row, col.accessor)}
              </DataTableCell>
            );
          })}
        </DataTableRow>
      ))}
    </tbody>
  );
};

export default DataTableBody;
